import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Table, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { usersApiService } from '../../services/apiServices';
import { UserWithRolesDto, UserStatus } from '../../api-client';
import { stringToUserStatus, stringToRoleName } from '../utils/typeConverters';
import { getRoleNameRu } from '../../config/roleNames';
import { SmartPagination } from '../../components/SmartPagination';

const UsersListPage: React.FC = () => {
  const [users, setUsers] = useState<UserWithRolesDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Локальные состояния для текстовых полей (для управления вводом до нажатия Enter)
  const [emailInput, setEmailInput] = useState<string>('');
  const [usernameInput, setUsernameInput] = useState<string>('');
  const [fullNameInput, setFullNameInput] = useState<string>('');

  // Извлекаем параметры из URL - это единственный источник истины
  const emailFilter = searchParams.get('email') || '';
  const usernameFilter = searchParams.get('username') || '';
  const fullNameFilter = searchParams.get('fullName') || '';
  const statusFilter = searchParams.get('status') || '';
  const roleFilter = searchParams.get('roleName') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  // Синхронизируем локальные инпуты с URL при изменении фильтров
  useEffect(() => {
    setEmailInput(emailFilter);
  }, [emailFilter]);

  useEffect(() => {
    setUsernameInput(usernameFilter);
  }, [usernameFilter]);

  useEffect(() => {
    setFullNameInput(fullNameFilter);
  }, [fullNameFilter]);

  // Создаём стабильную функцию обновления URL
  const updateUrlParams = useCallback(
    (newParams: Partial<Record<string, string>>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          // Обновляем переданные параметры
          Object.entries(newParams).forEach(([key, value]) => {
            if (value === '' || value === null || value === undefined) {
              params.delete(key);
            } else {
              params.set(key, value);
            }
          });

          // Всегда устанавливаем page, если не передан явно
          if (!('page' in newParams)) {
            params.set('page', '1');
          }

          return params;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  // Ref для отслеживания текущих параметров запроса - предотвращает дублирование в StrictMode
  const requestParamsRef = useRef<string | null>(null);
  // Ref для отслеживания завершения запроса
  const isRequestInProgressRef = useRef<boolean>(false);

  // Загрузка пользователей - срабатывает только при изменении параметров URL
  useEffect(() => {
    // Сериализуем текущие параметры в строку для сравнения
    const paramsString = JSON.stringify({
      email: emailFilter,
      username: usernameFilter,
      fullName: fullNameFilter,
      status: statusFilter,
      role: roleFilter,
      page: currentPage,
    });

    // Если параметры не изменились с последнего запроса - пропускаем (защита от StrictMode)
    if (requestParamsRef.current === paramsString) {
      return;
    }

    // Если запрос уже выполняется - пропускаем (дополнительная защита)
    if (isRequestInProgressRef.current) {
      return;
    }

    requestParamsRef.current = paramsString;
    isRequestInProgressRef.current = true;
    setLoading(true);
    setError(null);

    const loadUsers = async () => {
      try {
        const response = await usersApiService.getUsers(
          limit,
          (currentPage - 1) * limit,
          emailFilter,
          usernameFilter,
          fullNameFilter,
          statusFilter ? stringToUserStatus(statusFilter) || undefined : undefined,
          roleFilter ? stringToRoleName(roleFilter) || undefined : undefined
        );

        setUsers(response.data || []);
      } catch (err: any) {
        console.error('Ошибка при загрузке пользователей:', err);
        setError(err.message || 'Ошибка при загрузке пользователей');
      } finally {
        isRequestInProgressRef.current = false;
        setLoading(false);
      }
    };

    loadUsers();

    // Cleanup при размонтировании или изменении зависимостей
    return () => {
      isRequestInProgressRef.current = false;
    };
  }, [emailFilter, usernameFilter, fullNameFilter, statusFilter, roleFilter, currentPage, limit]);

  // Обработчики изменений фильтров
  // Для текстовых полей - только по нажатию Enter
  const handleEmailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        updateUrlParams({ email: emailInput, page: '1' });
      }
    },
    [emailInput, updateUrlParams]
  );

  const handleUsernameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        updateUrlParams({ username: usernameInput, page: '1' });
      }
    },
    [usernameInput, updateUrlParams]
  );

  const handleFullNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        updateUrlParams({ fullName: fullNameInput, page: '1' });
      }
    },
    [fullNameInput, updateUrlParams]
  );

  // Для селектов - сразу при изменении
  const handleStatusChange = useCallback(
    (value: string) => {
      updateUrlParams({ status: value, page: '1' });
    },
    [updateUrlParams]
  );

  const handleRoleChange = useCallback(
    (value: string) => {
      updateUrlParams({ roleName: value, page: '1' });
    },
    [updateUrlParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', newPage.toString());
        return params;
      });
    },
    [setSearchParams]
  );

  // Обработчик сброса фильтров
  const handleResetFilters = useCallback(() => {
    setEmailInput('');
    setUsernameInput('');
    setFullNameInput('');
    updateUrlParams({ email: '', username: '', fullName: '', status: '', roleName: '', page: '1' });
  }, [updateUrlParams]);

  const getStatusBadgeVariant = (status: UserStatus) => {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'success';
      case UserStatus.PENDING:
        return 'warning';
      case UserStatus.BLOCKED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Управление пользователями</h2>
            <Link to="/users/create">
              <Button variant="primary">Создать пользователя</Button>
            </Link>
          </div>

          {/* Фильтры */}
          <div className="row mb-4">
            <div className="col-md-3 mb-2">
              <Form.Label>Фильтр по email</Form.Label>
              <Form.Control
                type="text"
                placeholder="Email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleEmailKeyDown}
              />
            </div>
            <div className="col-md-3 mb-2">
              <Form.Label>Фильтр по ФИО</Form.Label>
              <Form.Control
                type="text"
                placeholder="ФИО"
                value={fullNameInput}
                onChange={(e) => setFullNameInput(e.target.value)}
                onKeyDown={handleFullNameKeyDown}
              />
            </div>
            <div className="col-md-2 mb-2">
              <Form.Label>Фильтр по имени пользователя</Form.Label>
              <Form.Control
                type="text"
                placeholder="Имя пользователя"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                onKeyDown={handleUsernameKeyDown}
              />
            </div>
            <div className="col-md-2 mb-2">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">Все</option>
                <option value="active">Активный</option>
                <option value="pending">Ожидание</option>
                <option value="blocked">Заблокирован</option>
              </Form.Select>
            </div>
            <div className="col-md-2 mb-2">
              <Form.Label>Роль</Form.Label>
              <Form.Select value={roleFilter} onChange={(e) => handleRoleChange(e.target.value)}>
                <option value="">Все</option>
                <option value="user">Пользователь</option>
                <option value="admin">Администратор</option>
                <option value="accountant">Бухгалтер</option>
                <option value="hr">HR</option>
                <option value="recruiter">Рекрутер</option>
                <option value="lawyer">Юрист</option>
              </Form.Select>
            </div>
            <div className="col-md-2 mb-2 d-flex align-items-end">
              <Button variant="outline-secondary" className="w-100" onClick={handleResetFilters}>
                Сбросить
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Загрузка пользователей...</p>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>ФИО</th>
                    <th>Имя пользователя</th>
                    <th>Статус</th>
                    <th>Роли</th>
                    <th>Дата регистрации</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users && users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.email}</td>
                        <td>{user.profile?.full_name || '—'}</td>
                        <td>{user.username}</td>
                        <td>
                          <Badge bg={getStatusBadgeVariant(user.status)}>
                            {user.status === UserStatus.ACTIVE
                              ? 'Активный'
                              : user.status === UserStatus.PENDING
                                ? 'Ожидание'
                                : 'Заблокирован'}
                          </Badge>
                        </td>
                        <td>
                          {user.roles
                            ?.slice()
                            .sort((a, b) =>
                              getRoleNameRu(a.name).localeCompare(getRoleNameRu(b.name))
                            )
                            .map((role) => (
                              <Badge key={role.name} bg="info" className="me-1">
                                {getRoleNameRu(role.name)}
                              </Badge>
                            ))}
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>
                          <Link to={`/users/${user.id}`}>
                            <Button variant="outline-primary" size="sm">
                              Подробнее
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center">
                        Пользователи не найдены
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Пагинация */}
              <SmartPagination
                currentPage={currentPage}
                limit={limit}
                currentItemsCount={users.length}
                onPageChange={handlePageChange}
                siblingCount={1}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default UsersListPage;
